import React from 'react';
import { motion } from 'framer-motion';
import { Plus, Minus, Trash2, UtensilsCrossed } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { ZoomableImage } from './ZoomableImage';

export const DishCard = ({ dish, showAddToCart = true }) => {
  const { addToCart } = useCart();

  const handleAddToCart = async () => {
    try {
      await addToCart(dish.dish_id, 1);
      toast.success(`${dish.name} ajouté au panier`);
    } catch (error) {
      toast.error('Connectez-vous pour commander');
    }
  };

  const categoryLabels = {
    vegetarian: 'Végétarien',
    meat: 'Viande',
    fish: 'Poisson',
    dessert: 'Dessert',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-product group"
      data-testid={`dish-card-${dish.dish_id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        {dish.image_url ? (
          <ZoomableImage
            src={dish.image_url}
            alt={dish.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#F0C39A] to-[#D88A5A] group-hover:from-[#E8B384] group-hover:to-[#C97A4A] transition-colors">
            <UtensilsCrossed className="text-white/70" size={56} strokeWidth={1.5} />
          </div>
        )}
        <div className="absolute top-4 left-4 flex flex-wrap gap-2">
          {dish.category === 'vegetarian' && (
            <span className="badge-vegetarian">
              {categoryLabels[dish.category]}
            </span>
          )}
          {dish.is_featured && (
            <span className="badge-featured">Coup de coeur</span>
          )}
        </div>
      </div>
      
      <div className="p-5 space-y-3">
        <div className="flex justify-between items-start">
          <h3 className="font-heading text-xl font-semibold text-navy">
            {dish.name}
          </h3>
        </div>
        
        <p className="text-navy/60 text-sm line-clamp-2">
          {dish.description}
        </p>
        
        {showAddToCart && (
          <button
            onClick={handleAddToCart}
            className="w-full btn-primary text-sm py-2.5 mt-4"
            data-testid={`add-to-cart-${dish.dish_id}`}
          >
            Ajouter au panier
          </button>
        )}
      </div>
    </motion.div>
  );
};

export const CartItemCard = ({ item }) => {
  const { updateCartItem, removeFromCart } = useCart();

  const handleQuantityChange = async (newQuantity) => {
    if (newQuantity < 1) {
      await removeFromCart(item.dish_id);
      toast.success('Article supprimé');
    } else {
      await updateCartItem(item.dish_id, newQuantity);
    }
  };

  return (
    <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-stone-200">
      <ZoomableImage
        src={item.dish?.image_url}
        alt={item.dish?.name}
        className="w-20 h-20 object-cover rounded-lg"
      />
      
      <div className="flex-1 min-w-0">
        <h4 className="font-heading font-semibold text-navy truncate">
          {item.dish?.name}
        </h4>
        <p className="text-terracotta font-medium">
          {item.dish?.price?.toFixed(2)} CHF
        </p>
      </div>
      
      <div className="flex items-center gap-2">
        <button
          onClick={() => handleQuantityChange(item.quantity - 1)}
          className="p-2 text-navy hover:text-terracotta hover:bg-terracotta/10 rounded-full transition-colors"
          data-testid={`decrease-quantity-${item.dish_id}`}
        >
          <Minus size={18} />
        </button>
        <span className="w-8 text-center font-medium text-navy">
          {item.quantity}
        </span>
        <button
          onClick={() => handleQuantityChange(item.quantity + 1)}
          className="p-2 text-navy hover:text-terracotta hover:bg-terracotta/10 rounded-full transition-colors"
          data-testid={`increase-quantity-${item.dish_id}`}
        >
          <Plus size={18} />
        </button>
      </div>
      
      <div className="text-right">
        <p className="font-bold text-navy">
          {item.item_total?.toFixed(2)} CHF
        </p>
        <button
          onClick={() => removeFromCart(item.dish_id)}
          className="p-1 text-red-500 hover:text-red-600 transition-colors"
          data-testid={`remove-item-${item.dish_id}`}
        >
          <Trash2 size={18} />
        </button>
      </div>
    </div>
  );
};
